/** @jsxImportSource @emotion/react */

import { css } from '@emotion/react';
import classNames from 'classnames';
import { Search } from 'lucide-react';
import { ApplicationStatus } from 'modules/teams/domain/enums/application-status';
import { ChangeEvent, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { theme } from 'styles/theme/theme';
import UDInput from 'ud-form/components/input/component';
import { UDButton } from 'ud-ui/components';
import { useAsyncButton } from 'ud-ui/hooks/use-async-button';
import { AnnouncedTeamStatus } from 'modules/teams/domain/enums/announced-team-status';
import FilterButton from './filter-button';

export type ChampionshipApplicationFiltersOption<StatusType> = {
  status: StatusType;
  color: string;
};

export type ChampionshipTeamsFilterValues<StatusType> = {
  name: string;
  status: StatusType;
};

type ChampionshipApplicationFiltersProps<StatusType> = {
  selectedStatus: StatusType;
  name?: string;
  options: ChampionshipApplicationFiltersOption<StatusType>[];
  inputPlaceholder?: string;
  countByStatus?: {
    [key: string]: number;
  };
  onSelect?: (status: AnnouncedTeamStatus) => void;
  onChange?: (values: ChampionshipTeamsFilterValues<StatusType>) => void;
  className?: string;
  selectedIds?: number[];
  onAccept?: () => Promise<void> | void;
  onDecline?: () => Promise<void> | void;
};

function ChampionshipApplicationFilters<StatusType>(props: ChampionshipApplicationFiltersProps<StatusType>) {
  const {
    selectedStatus,
    name = '',
    options,
    inputPlaceholder,
    countByStatus,
    className,
    onChange,
    selectedIds = [],
    onAccept,
    onDecline,
  } = props;

  const { t } = useTranslation();

  const handleSelect = useCallback(
    (status: StatusType) => () => {
      if (onChange && status !== selectedStatus) {
        onChange({
          name,
          status,
        });
      }
    },
    [onChange, selectedStatus, name],
  );

  const handleOnChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (onChange) {
        onChange({
          name: event.target.value,
          status: selectedStatus,
        });
      }
    },
    [onChange, selectedStatus],
  );

  const { isLoading: isAcceptLoading, handleClick: onClickAccept } = useAsyncButton(onAccept, {
    onError: (error) => {
      if (error.message.includes('limit exceeded')) {
        const regex = /(\d+)/g;
        const limit = error.message.match(regex)?.[0] ?? '-';
        toast.error(`Превышен лимит заявок на участие в чемпионате. По условиям доступно заявок на команду: ${limit}`);
      } else {
        toast.error('Ошибка при принятии игроков');
      }
    },
  });
  const { isLoading: isDeclineLoading, handleClick: onClickDecline } = useAsyncButton(onDecline, {
    onError: () => toast.error('Ошибка при отклонении игроков'),
    onSuccess: () => toast.success('Игроки отклонены'),
  });

  const showAcceptButton = selectedStatus === ApplicationStatus.PENDING || selectedStatus === ApplicationStatus.DECLINED;
  const showDeclineButton = selectedStatus === ApplicationStatus.PENDING || selectedStatus === ApplicationStatus.ACCEPTED;

  return (
    <div className={classNames('flex items-center', className)}>
      <div className="flex" css={cssBlockButtons(selectedIds.length > 0, showAcceptButton, showDeclineButton)}>
        {showAcceptButton && (
          <UDButton
            style={{ width: 116, minWidth: 116, height: 36 }}
            variant="primary"
            onClick={onClickAccept}
            disabled={isDeclineLoading}
            loading={isAcceptLoading}
          >
            Принять
          </UDButton>
        )}
        {showDeclineButton && (
          <UDButton
            style={{ width: 116, minWidth: 116, height: 36 }}
            variant="secondary"
            onClick={onClickDecline}
            disabled={isAcceptLoading}
            loading={isDeclineLoading}
          >
            Отклонить
          </UDButton>
        )}
      </div>
      <div style={{ width: 300 }}>
        <UDInput
          style={{ border: '1px solid #E7E7E7' }}
          value={name}
          placeholder={inputPlaceholder}
          iconProps={{
            position: 'icon-left',
            component: <Search size={16} color={theme.colors.SurfaceRomanSilver30} />,
          }}
          onChange={handleOnChange}
        />
      </div>

      <div className="ml-auto flex">
        {options.map((item) => {
          const isActive = item.status === selectedStatus;
          const statusString = item.status as any as string;

          return (
            <FilterButton key={statusString} className={classNames({ active: isActive })} onClick={handleSelect(item.status)}>
              <span className="text subhead ml-2">
                {t(`applications.statusFilter.${item.status}`)}
                {countByStatus && countByStatus[statusString] !== undefined && (
                  <span className="text ml-1" style={{ color: item.color }}>
                    ({countByStatus[statusString]})
                  </span>
                )}
              </span>
            </FilterButton>
          );
        })}
      </div>
    </div>
  );
}

export default ChampionshipApplicationFilters;

const cssBlockButtons = (isActive: boolean, showAcceptButton: boolean, showDeclineButton: boolean) => css`
  gap: 16px;
  overflow: hidden;

  transition:
    padding ${isActive ? '0s' : '0.25s'} ease-in-out,
    margin-right 0.25s ease-in-out,
    width 0.25s ease-in-out;

  padding: ${isActive ? '1px' : '0'};
  margin-right: ${isActive ? '20px' : '0'};
  width: ${isActive ? calcWidth(showAcceptButton, showDeclineButton) : '0'};
`;

const calcWidth = (showAcceptButton: boolean, showDeclineButton: boolean) => {
  if (showAcceptButton && showDeclineButton) {
    return '250px';
  }
  if (showAcceptButton) {
    return '118px';
  }
  if (showDeclineButton) {
    return '118px';
  }
  return '0';
};
